<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSubscriptionStore } from '@/store/subscription/subscriptionStore'
import createHttpClient from '@/api/httpClient'
import MessageService from '@/components/feedback/message/messageService'
import Address from '@/components/inputs/Address.vue'
import { TvaNumberRegex } from '@/utils/regex'
import { country, getCountries } from '@/api/account/account'

const subscriptionStore = useSubscriptionStore()
const httpClient = createHttpClient()

const { t } = useI18n()
const emit = defineEmits(['update:company', 'update:close'])
const isFocused = ref(false)
const isHovered = ref(false)

const requiredRules = [v => !!v || `${t('dashboard.subscription.required')}`]
const loading = ref(false)
const globalErrors = ref()

const form = ref({
  nameCompany: '',
  siret: '',
  addressCompany: '',
  countryCompany: '',
  postalCodeCompany: '',
  cityCompany: '',
  tvaNumberCompany: '',
})

const isValidForm = ref(false)
const isInitializing = ref(false)
const validVat = ref(false)
const addressErrors = ref()
const numberError = ref('')
const formErrors: any = ref({})
const countries = ref()

const clearError = field => {
  if (formErrors.value[field]) {
    formErrors.value[field] = null
  }
}

const onSubmit = async () => {
  globalErrors.value = ''
  addressErrors.value = ''

  if (!isValidForm.value) return false

  if (loading.value) return false
  loading.value = true

  try {
    const { data } = await httpClient.post(
      '/account/billing-informations',
      form.value,
    )
    MessageService.success(
      t('dashboard.subscription.messages.informations.success'),
    )
    await subscriptionStore.fetchSubscriptionStatus() // fetch subscription status
    subscriptionStore.displayBillingModal = false
    await subscriptionStore.fetchInfoData()
  } catch (error) {
    MessageService.error(
      t('dashboard.subscription.messages.informations.error'),
    )

    addressErrors.value = ''
    if (error.response?.status === 422) {
      if ('addressCompany' in error.response?.data) {
        addressErrors.value += '\n' + error.response?.data?.addressCompany?.[0]
      }
      if ('postalCodeCompany' in error.response?.data) {
        addressErrors.value +=
          '\n' + error.response?.data?.postalCodeCompany?.[0]
      }
      if ('cityCompany' in error.response?.data) {
        addressErrors.value += '\n' + error.response?.data?.cityCompany?.[0]
      }

      formErrors.value = error.response?.data
    }

    globalErrors.value = error.response?.data?.message
  } finally {
    loading.value = false
  }
}

const tva = ref(0)

watch(
  () => form.value.countryCompany,
  async () => {
    if (isInitializing.value) return false
    if (form.value.tvaNumberCompany) {
      tva.value = 0
    } else {
      tva.value = await subscriptionStore.tvaByCountry(
        form.value.countryCompany,
      )
    }
  },
)

watch(
  () => subscriptionStore.displayBillingModal,
  () => {
    isInitializing.value = true

    setTimeout(() => {
      isInitializing.value = false
    }, 0)
  },
  { immediate: true },
)

watch(
  () => form.value.tvaNumberCompany,
  async v => {
    if (isInitializing.value) return false
    numberError.value = ''

    if (form.value.tvaNumberCompany) {
      const value = v.replace(/\s+/g, '')

      validVat.value = TvaNumberRegex.test(value)

      if (validVat.value) {
        tva.value = 0
      } else {
        numberError.value = t('incorrect_number')
      }
    } else {
      tva.value = await subscriptionStore.tvaByCountry(
        form.value.countryCompany,
      )
    }
  },
  { flush: 'post' },
)

onMounted(async () => {
  if (!subscriptionStore.userInformation) return
  form.value.nameCompany = subscriptionStore.userInformation.nameCompany
  form.value.countryCompany = subscriptionStore.userInformation.countryCompany
  form.value.siret = subscriptionStore.userInformation.siret
  form.value.addressCompany = subscriptionStore.userInformation.addressCompany
  form.value.tvaNumberCompany =
    subscriptionStore.userInformation.tvaNumberCompany
  form.value.cityCompany = subscriptionStore.userInformation.cityCompany
  form.value.postalCodeCompany =
    subscriptionStore.userInformation.postalCodeCompany

  tva.value = subscriptionStore.subscriptionStatus['tva']

  const res: country[] = await getCountries()
  countries.value = res
})
</script>

<template>
  <v-dialog
    :model-value="subscriptionStore.displayBillingModal"
    persistent
    max-width="600"
    transition="dialog-top-transition"
  >
    <v-card color="white" rounded="xl">
      <v-card-title
        class="py-3 d-flex justify-center w-full align-center bg-surface-lighten-1"
      >
        <span class="font-weight-bold title">{{
          t('dashboard.subscription.update_information_modal_title')
        }}</span>
        <img
          style="position: absolute; right: 10px"
          class="cursor-pointer mr-2"
          @click="subscriptionStore.displayBillingModal = false"
          src="@/assets/_close.svg"
        />
      </v-card-title>

      <v-form @submit.prevent="onSubmit" v-model="isValidForm">
        <v-card-text class="pa-8">
          <div>
            <label>{{ t('dashboard.subscription.company_label') }}</label>
            <v-text-field
              rounded="lg"
              density="compact"
              v-model="form.nameCompany"
              :error-messages="formErrors?.nameCompany"
              @input="clearError('nameCompany')"
              type="text"
            ></v-text-field>
          </div>

          <div>
            <label>{{ t('dashboard.subscription.siret') }}</label>

            <v-text-field
              rounded="lg"
              density="compact"
              v-model="form.siret"
              :error-messages="formErrors?.siret"
              @input="clearError('siret')"
              type="text"
            ></v-text-field>
          </div>

          <div>
            <div class="d-flex align-center">
              <label>{{ t('dashboard.subscription.tva_number') }}</label>
              <v-tooltip :text="$t('register.tva_infobulle')">
                <template v-slot:activator="{ props }">
                  <v-icon
                    size="x-small"
                    class="cursor-pointer"
                    color="#757575"
                    v-bind="props"
                    >mdi-information</v-icon
                  >
                </template>
              </v-tooltip>
            </div>
            <v-text-field
              rounded="lg"
              density="compact"
              v-model="form.tvaNumberCompany"
              :error-messages="numberError"
              @input="clearError('tvaNumberCompany')"
              type="text"
              clearable
            ></v-text-field>
          </div>

          <div class="d-sm-flex ga-sm-2">
            <div class="w-sm-50 w-100">
              <label>{{ $t('dashboard.addresses.selectCountry') }}</label>
              <v-autocomplete
                rounded="lg"
                density="compact"
                class="mb-5"
                v-model="form.countryCompany"
                :items="countries"
                item-title="label"
                :rules="requiredRules"
                clearable
                hide-details="auto"
              />
            </div>

            <div class="w-sm-50 w-100">
              <label>% de TVA</label>

              <v-text-field
                readonly
                rounded="lg"
                v-model="tva"
                class="text-disabled"
                density="compact"
                variant="outlined"
              />
            </div>
          </div>

          <div class="">
            <Address v-model="form" />

            <template v-if="false">
              <label>{{ t('register.billing_address') }}</label>

              <v-card
                color="white"
                elevation="0"
                :class="{ 'border-focused': isFocused || isHovered }"
                class="border-card"
                @mouseover="isHovered = true"
                @mouseleave="isHovered = false"
              >
                <v-card-text border class="pa-0">
                  <div class="pa-3">
                    <input
                      class="pa-0 no-focus w-100"
                      :placeholder="$t('register.billing_address_placeholder')"
                      v-model="form.addressCompany"
                      :error-messages="formErrors?.addressCompany"
                      @input="clearError('addressCompany')"
                      @focus="isFocused = true"
                      @blur="isFocused = false"
                      type="text"
                    />
                  </div>
                  <v-divider color="#b2aeae" opacity="2" />
                  <div class="d-sm-flex address">
                    <div class="w-sm-50 border-custom pa-3">
                      <input
                        class="pa-0 no-focus w-100"
                        :placeholder="$t('register.postal_code')"
                        v-model="form.postalCodeCompany"
                        :error-messages="formErrors?.postalCodeCompany"
                        @input="clearError('postalCodeCompany')"
                        @focus="isFocused = true"
                        @blur="isFocused = false"
                      />
                    </div>
                    <div class="w-sm-50 pa-3">
                      <input
                        class="pa-0 no-focus w-100"
                        :placeholder="$t('register.city')"
                        v-model="form.cityCompany"
                        :error-messages="formErrors?.cityCompany"
                        @input="clearError('cityCompany')"
                        @focus="isFocused = true"
                        @blur="isFocused = false"
                        type="text"
                      />
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </template>

            <v-row>
              <v-col :cols="12">
                <p class="text-red text-caption text-center">
                  {{ addressErrors }}
                </p>
              </v-col>
            </v-row>
          </div>
          <p class="text-red py-2 text-caption text-center">
            {{ globalErrors }}
          </p>

          <div class="d-sm-flex mt-5 ga-2">
            <v-btn
              @click="subscriptionStore.displayBillingModal = false"
              class="w-sm-50 w-100"
              color="#E0E0E0"
            >
              <span class="text-gray-80">Annuler</span>
            </v-btn>

            <v-btn
              :loading="loading"
              type="submit"
              color="secondary"
              class="w-sm-50 w-100 mt-sm-0 mt-2"
              >{{ t('dashboard.subscription.saveBtn') }}</v-btn
            >
          </div>
        </v-card-text>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.title {
  color: #007058;
  font-weight: 600px;
  font-size: 20px;
  line-height: 32.2px;
}

.border-custom {
  border-right: 1px solid #b2aeae;
}

.no-focus:focus {
  outline: none;
  box-shadow: none;
}

.border-custom {
  border-right: 1px solid #b2aeae;
}

.border-card {
  border: 1.5px solid #b2aeae;
  transition: border-color 0.3s ease-in-out;
}

.border-focused {
  border-color: #3b3b3b !important;
  border: 1.8px solid #3b3b3b;
}

label {
  font-size: 16px;
  font-weight: 600;
  line-height: 140%;
  color: #616161;
  letter-spacing: -0.64;
}

@media (max-width: 599px) {
  .border-custom {
    border-right: none;
    border-bottom: 1px solid #b2aeae;
  }
}
</style>
